/**
 * Poda de borradores de inspección vencidos.
 *
 * EL DEFECTO QUE ESTO CORRIGE (lentitud acumulativa, se "arreglaba"
 * reinstalando). `getDraft` trataba un borrador viejo como inexistente, pero
 * NADIE lo quitaba del mapa persistido: cada entrega o devolución dejaba su
 * entrada —quince fotos, odómetro, notas y DOS FIRMAS— para siempre dentro del
 * único blob que Zustand reescribe completo en cada cambio. Con los meses ese
 * blob crece a megabytes; cada tecla, cada foto y cada trazo de firma lo
 * serializan entero hacia MMKV, y la app se siente lenta en equipos de gama
 * media hasta que reinstalar borra los datos. Un borrador solo vale seis horas:
 * lo que ya no vale se poda al guardar y al rehidratar.
 *
 * Módulo puro a propósito: sin React, sin MMKV, sin react-native — así el
 * invariante se prueba en node sin montar la app.
 */
import type { InspectionDraft } from './checkin.store'

/**
 * Seis horas, igual que el borrador de reserva.
 *
 * Un traspaso dura minutos; si un borrador sobrevive seis horas es porque algo
 * salió mal, y resucitar el odómetro de ayer sobre la inspección de hoy sería
 * peor que empezar limpio.
 */
export const INSPECTION_DRAFT_TTL_MS = 6 * 60 * 60 * 1000

export function isInspectionDraftFresh(
  draft: InspectionDraft | undefined,
  now: number,
): draft is InspectionDraft {
  return Boolean(draft && now - draft.updatedAt <= INSPECTION_DRAFT_TTL_MS)
}

/**
 * Devuelve un mapa solo con los borradores frescos. Devuelve la MISMA
 * referencia cuando no hay nada que podar: `persist` escribe en cada `set()` y
 * un mapa sin cambios no debe costar una escritura a disco.
 */
export function pruneExpiredDrafts(
  drafts: Record<string, InspectionDraft>,
  now: number = Date.now(),
): Record<string, InspectionDraft> {
  const keys = Object.keys(drafts)
  const fresh: Record<string, InspectionDraft> = {}
  for (const key of keys) {
    const draft = drafts[key]
    if (isInspectionDraftFresh(draft, now)) fresh[key] = draft!
  }
  return Object.keys(fresh).length === keys.length ? drafts : fresh
}
